import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { UtensilsCrossed, Bell, BellOff, CheckCircle2, AlertTriangle, Menu as MenuIcon, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatPrice } from '@/utils/currency';
import { isRtl, t } from '@/utils/i18n';
import { LanguagePicker, detectInitialLang } from '@/components/LanguagePicker';
import { EntertainmentButtons, MiniGamesSheet, AiPlaceholderSheet } from './CustomerEntertainment';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = {
  warm:     { primary: '#EA580C', primaryLight: '#FFEDD5', bg: '#FAFAF9', accent: '#C2410C' },
  ocean:    { primary: '#0284C7', primaryLight: '#E0F2FE', bg: '#F0F9FF', accent: '#0369A1' },
  forest:   { primary: '#16A34A', primaryLight: '#DCFCE7', bg: '#F0FDF4', accent: '#15803D' },
  midnight: { primary: '#4F46E5', primaryLight: '#EEF2FF', bg: '#F5F3FF', accent: '#4338CA' },
  rose:     { primary: '#E11D48', primaryLight: '#FFE4E6', bg: '#FFF1F2', accent: '#BE123C' },
  amber:    { primary: '#D97706', primaryLight: '#FEF3C7', bg: '#FFFBEB', accent: '#B45309' },
};

// ---------- Platform detection ----------
// Returns one of: 'ios-safari', 'android', 'desktop', 'other'. iOS Chrome /
// Firefox / Edge all use WebKit under the hood and have the same Vibration
// API limitations as Safari, so we treat them identically.
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios-safari';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/i.test(ua) && !/Mobile/.test(ua)) return 'desktop';
  return 'other';
}

// Base64-url-decode helper for VAPID public key → Uint8Array (required by
// PushManager.subscribe).
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

function RestaurantLogo({ logoPath, theme, size = 96 }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logoPath]);
  if (logoPath && !errored) {
    return (
      <img
        src={`${API}/files/${logoPath}`}
        alt="Restaurant logo"
        onError={() => setErrored(true)}
        style={{ width: size, height: size, objectFit: 'contain' }}
        className="rounded-2xl bg-white shadow-sm"
      />
    );
  }
  return (
    <div className="rounded-2xl flex items-center justify-center" style={{ width: size, height: size, backgroundColor: theme.primaryLight }}>
      <UtensilsCrossed size={size * 0.45} style={{ color: theme.primary }} />
    </div>
  );
}

export default function PagingCustomerView() {
  const { restaurantId, pagingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);            // { paging, restaurant }
  const [entOpen, setEntOpen] = useState(null);
  // View-only menu sheet — host can toggle this on in Paging settings. We
  // lazy-load the menu the first time the customer taps "View Menu" so we
  // don't waste bandwidth for restaurants who keep this off.
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuData, setMenuData] = useState(null); // { categories, items } | null

  // Alert / push activation state
  const [alertsActivated, setAlertsActivated] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showReadyBanner, setShowReadyBanner] = useState(false);
  // Sticky in-app toast that persists AFTER the customer dismisses the
  // fullscreen banner. Stays visible until the order moves out of the
  // "ready" state — guarantees the customer can't miss the alert even if
  // they swipe the fullscreen banner away while looking at the menu.
  const [readyToastDismissed, setReadyToastDismissed] = useState(false);
  // iOS-only PWA install hint. Web Push on iPhone only works when the
  // customer has added the page to their Home Screen first; this banner
  // surfaces that one-time setup.
  const [showIosInstallHint, setShowIosInstallHint] = useState(false);

  // i18n state — must be declared BEFORE any conditional early return to
  // satisfy React's rules-of-hooks. We default to 'en' before the restaurant
  // payload lands; an effect below re-seeds once `data` is available.
  const [lang, setLangState] = useState('en');

  const audioCtxRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const lastStatusRef = useRef(null);
  const lastAlertAtRef = useRef(null);
  const platform = detectPlatform();

  // Eagerly register the service worker the moment the paging page loads
  // (independent of whether the customer taps "Activate Alerts"). This:
  //   1. Lets iOS Safari surface the "Add to Home Screen" affordance now,
  //      which is the only path to Web Push on iOS.
  //   2. Means a push that arrives a few seconds after Activate has a
  //      ready, claimed SW to deliver it through.
  //   3. Sets up a `message` channel so when the SW receives a push while
  //      the page is in the foreground / locked-but-just-woken, the page
  //      itself can also fire the in-app beep + sticky toast even if the
  //      browser suppresses the OS notification (some Android OEMs do).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelled = false;
    (async () => {
      try {
        await navigator.serviceWorker.register('/sw.js');
        if (cancelled) return;
        const onMsg = (event) => {
          if (event.data && event.data.type === 'paging-push') {
            // SW received a push while the page is alive — fire the in-app
            // alert sequence so the customer gets a double cue.
            triggerReadyAlert();
          }
        };
        navigator.serviceWorker.addEventListener('message', onMsg);
      } catch { /* registration failures are non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show an iOS install hint when running in Safari NOT yet installed as
  // a PWA. The hint stays dismissable per-session and disappears once the
  // user is in standalone mode.
  useEffect(() => {
    if (platform !== 'ios-safari') return;
    // navigator.standalone is iOS-Safari-specific; matches() covers PWA
    // launchers on iPadOS 13+.
    const isStandalone = (window.navigator.standalone === true) ||
      window.matchMedia?.('(display-mode: standalone)').matches;
    if (isStandalone) return;
    try {
      if (sessionStorage.getItem('olafel_ios_hint_dismissed') === '1') return;
    } catch { /* ignore */ }
    setShowIosInstallHint(true);
  }, [platform]);

  // Clear the sticky toast dismissal flag whenever:
  //   • the order leaves the "ready" state (cancelled / served / refund), OR
  //   • the host fires a fresh "Re-alert" (last_alert_at changes while
  //     status stays "ready").
  // Without this second branch a customer who dismissed the toast once
  // would never see it again even if the host pressed Re-alert because
  // they missed pickup the first time.
  const lastAlertSeenRef = useRef(null);
  useEffect(() => {
    const status = data?.paging?.status;
    const alertAt = data?.paging?.last_alert_at || null;
    if (status !== 'ready') {
      setReadyToastDismissed(false);
      lastAlertSeenRef.current = null;
      return;
    }
    if (lastAlertSeenRef.current && alertAt && alertAt !== lastAlertSeenRef.current) {
      // Re-alert fired — surface the toast again.
      setReadyToastDismissed(false);
    }
    lastAlertSeenRef.current = alertAt;
  }, [data?.paging?.status, data?.paging?.last_alert_at]);


  // ---------- Fetch + poll the paging record ----------
  // `silent` = true is passed during polling — never flips the page to the
  // error screen on a single failed fetch (screen sleep, throttling, etc).
  // We only show "Token not found" if the FIRST fetch fails.
  const fetchOnce = useCallback(async (silent = false) => {
    try {
      const langQ = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      const [{ data: pagingData }, restEff] = await Promise.all([
        axios.get(`${API}/customer/paging/${pagingId}${langQ}`, { timeout: 8000 }),
        axios.get(`${API}/customer/${restaurantId}${langQ}`, { timeout: 8000 }).catch(() => ({ data: null })),
      ]);
      setData({
        paging: pagingData.paging,
        restaurant: { ...pagingData.restaurant, ...(restEff.data || {}) },
      });
      return pagingData.paging;
    } catch {
      if (!silent) setError('Token not found');
      return null;
    }
  }, [pagingId, restaurantId, lang]);

  // Centralised "check current state, maybe alert" so we can call this from
  // the interval AND from the visibilitychange handler (when the user wakes
  // their screen we re-fetch immediately instead of waiting up to 5 s).
  const checkAndMaybeAlert = useCallback(async () => {
    const p = await fetchOnce(true);
    if (!p) return;
    const prevStatus = lastStatusRef.current;
    const prevAlertAt = lastAlertAtRef.current;
    lastStatusRef.current = p.status;
    lastAlertAtRef.current = p.last_alert_at || null;
    // Fire the alert sequence when:
    //   (a) status JUST flipped TO ready (first time), OR
    //   (b) status is ready AND host pressed Re-alert (last_alert_at changed)
    const firstReady = p.status === 'ready' && prevStatus !== 'ready';
    const reAlerted  = p.status === 'ready' && prevAlertAt && p.last_alert_at && p.last_alert_at !== prevAlertAt;
    if (firstReady || reAlerted) triggerReadyAlert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnce]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    (async () => {
      const p = await fetchOnce(false);
      if (!cancelled) {
        lastStatusRef.current = p?.status || null;
        lastAlertAtRef.current = p?.last_alert_at || null;
        setLoading(false);
      }
    })();
    timer = setInterval(() => {
      if (!cancelled) checkAndMaybeAlert();
    }, 5000);
    // The moment the user wakes their screen / switches back to this tab,
    // re-fetch IMMEDIATELY so the alert fires within a second.
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkAndMaybeAlert();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      stopBeep();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagingId, restaurantId]);

  // Seed the customer's language once the restaurant payload lands. Auto-
  // detects the browser language when the host has translated into it,
  // otherwise falls back to the restaurant default. Persisted choice in
  // localStorage always wins.
  useEffect(() => {
    const r = data?.restaurant;
    if (!r) return;
    const def = r.default_language || 'en';
    const available = r.translation_languages || (r.secondary_language ? [r.secondary_language] : []);
    setLangState(detectInitialLang({ restaurantId, defaultLang: def, availableLangs: available }));
  }, [data, restaurantId]);

  // Re-fetch the restaurant + paging payload whenever the customer flips
  // languages so the host-typed `paging_message_text` swaps to the
  // translated copy. We skip the very first run (loading=true) since the
  // initial fetch above already used the seeded language.
  useEffect(() => {
    if (loading) return;
    fetchOnce(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // When the customer flips language WHILE the menu sheet is open (or has
  // been opened previously and cached an older language), refetch the menu
  // so it reflects the new selection without needing to close + reopen the
  // sheet. Without this, switching EN → ZH leaves the sheet showing the
  // language that was active when "View Menu" was first tapped.
  useEffect(() => {
    if (!menuOpen) return;
    if (menuData && menuData.__lang === lang) return;
    let cancelled = false;
    (async () => {
      setMenuLoading(true);
      try {
        const res = await axios.get(`${API}/customer/${restaurantId}/menu?lang=${encodeURIComponent(lang)}`);
        if (!cancelled) setMenuData({ ...(res.data || { categories: [], items: [] }), __lang: lang });
      } catch {
        if (!cancelled) setMenuData({ categories: [], items: [], __lang: lang });
      }
      if (!cancelled) setMenuLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, menuOpen]);

  // ---------- Alert sequence (vibrate + beep + banner) ----------
  const playBeep = () => {
    try {
      let ctx = audioCtxRef.current;
      if (!ctx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ctx = new Ctx();
        audioCtxRef.current = ctx;
      }
      // 3 short pulses, each 200ms, gap 100ms.
      const now = ctx.currentTime;
      for (let i = 0; i < 3; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, now + i * 0.30);
        gain.gain.exponentialRampToValueAtTime(0.4, now + i * 0.30 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.30 + 0.20);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.30);
        osc.stop(now + i * 0.30 + 0.22);
      }
    } catch { /* ignore */ }
  };
  const stopBeep = () => {
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
    // Cancel any in-flight vibration so "Stop alert" feels truly final.
    if (navigator.vibrate) { try { navigator.vibrate(0); } catch {} }
  };

  const triggerReadyAlert = () => {
    setShowReadyBanner(true);
    // Vibrate (Android only — Safari ignores).
    if (navigator.vibrate) {
      try { navigator.vibrate([500, 200, 500, 200, 500, 200, 500]); } catch {}
    }
    // If a beep cycle is already running (host pressed Re-alert), stop the
    // old one and start fresh so the operator's intent is honored.
    stopBeep();
    // AudioContext gets auto-suspended by the browser when the page is
    // backgrounded or the screen is locked. Resume() before each beep —
    // it's a no-op if already running.
    const resumeAndBeep = async () => {
      try {
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
          await audioCtxRef.current.resume();
        }
      } catch {}
      playBeep();
    };
    resumeAndBeep();
    // Keep alerting forever — beep + vibrate every 4 s until the customer
    // taps "Stop alert" (or the order moves out of `ready`). Hosts asked
    // for this so a customer who put the phone face-down can't miss it;
    // the foreground audio loop coexists with the OS push notification
    // which is already `requireInteraction:true`.
    beepIntervalRef.current = setInterval(() => {
      resumeAndBeep();
      if (navigator.vibrate) { try { navigator.vibrate([500, 200, 500]); } catch {} }
    }, 4000);
  };

  // Auto-stop the continuous beep/vibrate the moment the order status
  // moves out of `ready` (host pressed Served / Cancel / Refund) — without
  // this, a customer who put the phone in their pocket after dismissing
  // the banner would keep getting buzzed forever.
  useEffect(() => {
    if (data?.paging?.status !== 'ready') {
      stopBeep();
      setShowReadyBanner(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.paging?.status]);

  // ---------- Live WS subscription (replaces 5 s polling latency) ----------
  // Open a per-paging WebSocket as soon as the page loads. The backend
  // pushes `paging_status` events the instant the host taps Ready /
  // Re-alert / Served etc. so we react within ~50 ms. Polling stays as a
  // 30 s "heartbeat" fallback in case the WS drops on a flaky network.
  useEffect(() => {
    if (!pagingId) return;
    const wsBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, 'ws');
    const url = `${wsBase}/api/ws/paging/${pagingId}`;
    let ws;
    let retry;
    let cancelled = false;
    const connect = () => {
      try {
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type !== 'paging_status') return;
            // Fetch fresh state and decide whether to trigger an alert,
            // using the same first-ready / re-alert logic as the polling
            // path. This keeps a single source of truth (`checkAndMaybeAlert`).
            checkAndMaybeAlert();
          } catch { /* ignore malformed frames */ }
        };
        ws.onclose = () => {
          if (cancelled) return;
          // Auto-reconnect after a short delay so a transient network
          // blip can't leave the customer without live alerts.
          retry = setTimeout(connect, 2500);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch { /* ignore */ }
    };
    connect();
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      try { ws && ws.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagingId]);


  const activateAlerts = async () => {
    setActivating(true);
    try {
      // 1. Prime AudioContext (autoplay policies require user gesture).
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const c = audioCtxRef.current || new Ctx();
          audioCtxRef.current = c;
          if (c.state === 'suspended') await c.resume();
          // Silent tick so browsers count it as having user interaction.
          const osc = c.createOscillator();
          const gain = c.createGain();
          gain.gain.value = 0.0001;
          osc.connect(gain).connect(c.destination);
          osc.start(); osc.stop(c.currentTime + 0.03);
        }
      } catch {}
      // 2. Vibrate quickly to confirm permission on Android.
      if (navigator.vibrate) { try { navigator.vibrate([60, 40, 60]); } catch {} }
      setAlertsActivated(true);

      // 3. (Optional) Background Web Push subscription.
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const reg = await navigator.serviceWorker.register('/sw.js');
          await navigator.serviceWorker.ready;
          // Ask for notification permission AFTER service worker is ready
          // so the prompt feels tied to "Activate".
          const perm = await Notification.requestPermission();
          if (perm === 'granted') {
            const { data: keyData } = await axios.get(`${API}/push/vapid-public-key`);
            const applicationServerKey = urlBase64ToUint8Array(keyData.public_key);
            const sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey,
            });
            await axios.post(`${API}/customer/paging/${pagingId}/subscribe`, {
              subscription: sub.toJSON(),
            });
            setPushSubscribed(true);
          }
        } catch (e) {
          // Non-fatal — foreground alerts still work without Web Push.
          // eslint-disable-next-line no-console
          console.warn('Web push subscribe failed:', e);
        }
      }
    } finally {
      setActivating(false);
    }
  };

  // ---------- Render ----------
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (error || !data) return <div className="min-h-screen flex items-center justify-center text-stone-500">{error || 'Not found'}</div>;

  const restaurant = data.restaurant || {};
  const paging = data.paging || {};
  const theme = THEMES[restaurant.theme] || THEMES.warm;
  // i18n derived values — `lang` itself was declared at the top of the
  // component to satisfy hooks-order rules. Here we just compute the
  // available language codes and the helper functions from current state.
  const defaultLang   = restaurant.default_language   || 'en';
  const availableLangs = restaurant.translation_languages || (restaurant.secondary_language ? [restaurant.secondary_language] : []);
  const setLang = (code) => {
    setLangState(code);
    // NOTE: use the same `cust_lang:` storage key as the dine-in CustomerView
    // so `detectInitialLang` (which reads `cust_lang:…`) can restore the
    // customer's pick after a reload. Previously this wrote to
    // `paging_lang:…` which the detector never read — so a manual pick was
    // forgotten on every page refresh and the menu kept reverting to the
    // browser-detected language (e.g. Chinese on Chinese phones).
    try { localStorage.setItem(`cust_lang:${restaurantId}`, code); } catch { /* ignore */ }
  };
  const tr  = (k, vars) => t(k, lang, vars);
  const dir = isRtl(lang) ? 'rtl' : 'ltr';
  const bgPath = restaurant.customer_bg_image_path;
  const hasBg = !!bgPath;
  const rootStyle = hasBg
    ? {
        backgroundColor: theme.bg,
        backgroundImage: `url(${API}/files/${bgPath})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : { backgroundColor: theme.bg };

  const displayToken = paging.display_token || paging.token || '';
  const isCancelled = paging.status === 'cancelled' || paging.status === 'refunded' || paging.status === 'no_response';
  const isReady     = paging.status === 'ready';
  const isServed    = paging.status === 'served';

  // ----- Full-screen READY banner (covers everything) -----
  if (showReadyBanner) {
    return (
      <div
        data-testid="paging-ready-banner"
        dir={dir}
        className="fixed inset-0 z-50 flex flex-col items-center justify-center animate-pulse-slow"
        style={{
          background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
          color: '#fff',
        }}
        onClick={() => { stopBeep(); setShowReadyBanner(false); }}
      >
        <CheckCircle2 size={140} strokeWidth={2.2} className="drop-shadow-lg animate-bounce" />
        <h1 className="mt-6 text-5xl sm:text-6xl font-extrabold tracking-tight text-center px-6" style={{ fontFamily: 'Outfit' }}>
          {tr('order_ready')}
        </h1>
        <p className="mt-4 text-2xl sm:text-3xl font-semibold text-center px-6 max-w-md">
          {tr('collect_at_counter')}
        </p>
        <p className="mt-8 text-7xl font-extrabold tabular-nums">{displayToken}</p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); stopBeep(); setShowReadyBanner(false); }}
          data-testid="paging-ready-banner-stop"
          className="mt-10 px-6 py-3 rounded-full bg-white text-orange-700 font-extrabold text-base uppercase tracking-widest shadow-md hover:bg-orange-50 active:scale-95 transition"
        >
          {tr('stop_alert') || 'Stop alert'}
        </button>
        <p className="mt-3 text-xs uppercase tracking-widest opacity-70">{tr('ready_banner_tap')}</p>
      </div>
    );
  }

  return (
    <div
      data-testid="paging-customer-root"
      dir={dir}
      className="min-h-screen flex flex-col relative"
      style={rootStyle}
    >
      {hasBg && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(180deg, ${theme.bg}E6 0%, ${theme.bg}66 35%, ${theme.bg}66 65%, ${theme.bg}F2 100%)` }}
        />
      )}

      {/* Language picker — floating bottom-right pill that expands into a
          full-screen language wheel when tapped. Hidden automatically when
          the host hasn't translated into any secondary language. */}
      <LanguagePicker
        currentLang={lang}
        defaultLang={defaultLang}
        availableLangs={availableLangs}
        onChange={setLang}
        accent={theme.primary}
      />

      {/* Sticky READY toast — appears at the top whenever the order is
          marked ready. Survives the customer dismissing the fullscreen
          banner, only disappears once they manually X it OR the order
          moves to a different status (served/cancelled/refunded). Acts as
          a persistent in-page cue alongside the OS push notification.
          The Stop button kills the continuous beep+vibrate loop so a
          customer who put the phone down isn't left buzzing forever. */}
      {isReady && !readyToastDismissed && (
        <div
          data-testid="paging-ready-toast"
          role="alert"
          aria-live="assertive"
          className="sticky top-0 z-40 mx-3 mt-3 mb-1 rounded-2xl shadow-lg ring-2 ring-emerald-400/40 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 py-3 flex items-center gap-3 animate-pulse-slow"
        >
          <CheckCircle2 size={28} className="shrink-0 drop-shadow" />
          <div className="min-w-0 flex-1">
            <p className="font-extrabold text-base leading-tight">{tr('order_ready')}</p>
            <p className="text-sm opacity-95 leading-snug">{tr('collect_at_counter')}</p>
          </div>
          <button
            type="button"
            onClick={() => { stopBeep(); }}
            aria-label="Stop alert"
            data-testid="paging-ready-toast-stop"
            className="shrink-0 h-9 px-3 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 font-bold text-xs uppercase tracking-wider"
            title={tr('stop_alert') || 'Stop alert'}
          >
            {tr('stop_alert') || 'Stop'}
          </button>
          <button
            type="button"
            onClick={() => { stopBeep(); setReadyToastDismissed(true); }}
            aria-label="Dismiss"
            data-testid="paging-ready-toast-close"
            className="shrink-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* iOS install hint — Web Push on iPhone only works after the page
          is added to the Home Screen as a PWA. Hidden once installed, and
          dismissable per-session so we don't pester returning customers. */}
      {showIosInstallHint && (
        <div
          data-testid="paging-ios-install-hint"
          className="mx-3 mt-3 mb-1 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2 text-amber-900"
        >
          <Bell size={18} className="shrink-0 mt-0.5" />
          <p className="text-xs leading-snug flex-1">
            <span className="font-bold">Get alerts when locked: </span>
            tap <span className="font-bold">Share</span> → <span className="font-bold">Add to Home Screen</span>, then open Olafel from your Home Screen.
          </p>
          <button
            type="button"
            data-testid="paging-ios-install-close"
            onClick={() => {
              setShowIosInstallHint(false);
              try { sessionStorage.setItem('olafel_ios_hint_dismissed', '1'); } catch { /* ignore */ }
            }}
            aria-label="Dismiss hint"
            className="shrink-0 w-6 h-6 rounded-full hover:bg-amber-100 flex items-center justify-center"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="relative flex-1 flex flex-col items-center px-6 pt-10 pb-8">
        <div className="animate-fade-in-up mb-4">
          <RestaurantLogo logoPath={restaurant.logo_path} theme={theme} size={88} />
        </div>

        {restaurant.name && (
          <h1
            data-testid="paging-restaurant-name"
            className="text-2xl sm:text-3xl font-bold text-stone-900 text-center tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            {restaurant.name}
          </h1>
        )}

        <div className="mt-6 mb-4 text-center animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <p className="text-sm uppercase tracking-[0.18em] font-semibold text-stone-500">{tr('your_token')}</p>
          <p
            data-testid="paging-token-display"
            className="mt-1 text-7xl sm:text-8xl font-extrabold text-stone-900 tracking-tight tabular-nums leading-none"
            style={{ fontFamily: 'Outfit' }}
          >
            {displayToken}
          </p>
        </div>

        {/* Status display */}
        {isServed && (
          <div data-testid="paging-status-served" className="mt-2 px-4 py-2 rounded-full text-sm font-bold text-white" style={{ backgroundColor: theme.primary }}>
            {tr('order_collected')}
          </div>
        )}
        {isReady && !showReadyBanner && (
          <div data-testid="paging-status-ready" className="mt-2 px-5 py-3 rounded-xl text-base font-bold text-white shadow-md bg-emerald-600">
            {tr('order_ready')} {tr('collect_at_counter')}
          </div>
        )}
        {isCancelled && (
          <div data-testid="paging-status-cancelled" className="mt-2 px-4 py-2 rounded-full text-sm font-bold text-red-700 bg-red-50 border border-red-200">
            {tr('order_was', { status: tr(paging.status === 'no_response' ? 'st_no_response' : paging.status === 'cancelled' ? 'st_cancelled' : 'st_refunded') })}
          </div>
        )}
        {!isReady && !isServed && !isCancelled && (
          <p className="text-base sm:text-lg text-stone-700 text-center max-w-xs mx-auto mt-2 leading-snug">
            {restaurant.paging_message_text || tr('please_wait_alert')}
          </p>
        )}

        {/* Activate Alerts — only when waiting AND not yet activated */}
        {!isReady && !isServed && !isCancelled && !alertsActivated && (
          <div className="mt-5 w-full max-w-sm">
            <button
              type="button"
              data-testid="paging-activate-alerts-btn"
              onClick={activateAlerts}
              disabled={activating}
              className="w-full h-14 rounded-2xl bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-lg font-bold shadow-lg flex items-center justify-center gap-2 transition-colors"
            >
              <Bell size={20} /> {activating ? tr('activating') : tr('activate_alerts')}
            </button>

            {/* Platform-specific guidance — verbatim per spec */}
            {platform === 'ios-safari' ? (
              <div data-testid="paging-platform-hint-ios" className="mt-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <p>{tr('iphone_hint')}</p>
              </div>
            ) : (
              <div data-testid="paging-platform-hint-android" className="mt-3 px-4 py-3 rounded-xl border border-stone-200 bg-white/80 text-stone-700 text-sm">
                {tr('android_hint')}
              </div>
            )}
          </div>
        )}

        {alertsActivated && !isReady && !isServed && !isCancelled && (
          <div data-testid="paging-alerts-activated" className="mt-5 px-4 py-2 rounded-full text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 inline-flex items-center gap-2">
            <CheckCircle2 size={16} />
            {tr('alerts_on')}{pushSubscribed && ' · ' + tr('alerts_on_locked')}
          </div>
        )}

        {/* Read-only menu — only shown when the host has explicitly enabled
            it for paging in Settings → Show food menu, AND the customer has
            already activated alerts. Hiding it pre-activation keeps the
            "Activate Alerts" CTA + platform hint as the visual focus on
            arrival, then the menu replaces the hint area once activated so
            the customer has something to do while they wait. */}
        {restaurant.paging_show_menu && alertsActivated && !isReady && !isServed && !isCancelled && (
          <div className="mt-5 w-full max-w-sm">
            <button
              type="button"
              data-testid="paging-view-menu-btn"
              onClick={async () => {
                setMenuOpen(true);
                // Always re-fetch when the customer's chosen language differs
                // from what we last fetched (so flipping EN ↔ AR refreshes
                // the menu copy without needing to close + reopen the sheet).
                if (menuData && menuData.__lang === lang) return;
                setMenuLoading(true);
                try {
                  const res = await axios.get(`${API}/customer/${restaurantId}/menu?lang=${encodeURIComponent(lang)}`);
                  setMenuData({ ...(res.data || { categories: [], items: [] }), __lang: lang });
                } catch {
                  setMenuData({ categories: [], items: [], __lang: lang });
                }
                setMenuLoading(false);
              }}
              className="w-full h-14 rounded-2xl text-white text-lg font-bold shadow-lg flex items-center justify-center gap-2 transition-colors"
              style={{ backgroundColor: theme.primary, boxShadow: `0 6px 22px ${theme.primary}55` }}
            >
              <MenuIcon size={20} /> {tr('view_menu')}
            </button>
          </div>
        )}

        <div className="mt-8 w-full max-w-sm">
          <EntertainmentButtons enabled={restaurant.entertainment} onOpen={setEntOpen} tr={tr} />
        </div>

        <div className="mt-auto pt-8 pb-2 text-center" data-testid="paging-powered-by">
          <p className="text-xs text-stone-600">
            {tr('powered_by')}{' '}
            <a href="https://olafel.com" target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2 hover:opacity-80" style={{ color: theme.primary }}>
              Olafel.com
            </a>
          </p>
        </div>
      </div>

      <MiniGamesSheet open={entOpen === 'games'} onOpenChange={(o) => !o && setEntOpen(null)} />
      <AiPlaceholderSheet open={entOpen === 'quotes'} onOpenChange={(o) => !o && setEntOpen(null)} kind="quotes" restaurantId={restaurantId} />
      <AiPlaceholderSheet open={entOpen === 'poems'}  onOpenChange={(o) => !o && setEntOpen(null)} kind="poems"  restaurantId={restaurantId} />
      <AiPlaceholderSheet open={entOpen === 'jokes'}  onOpenChange={(o) => !o && setEntOpen(null)} kind="jokes"  restaurantId={restaurantId} />

      {/* Read-only menu sheet for paging customers. We render the same
          name / description / price / image layout the full customer view
          uses, but strip out every "add to cart" / quantity control —
          paging customers can only browse the menu, not order. */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="bottom"
          // Locked to 90vh on every device. shadcn's bottom variant has no
          // default height so without this the sheet would shrink-wrap its
          // content — fine on desktop, but on a phone the result is a tiny
          // 15%-tall strip glued to the bottom that looks broken even when
          // the menu has rendered correctly. h-[90vh] also lets the inner
          // table scroll naturally inside a known-sized container.
          className="h-[90vh] flex flex-col overflow-hidden rounded-t-3xl p-4"
          data-testid="paging-menu-sheet"
          style={{ backgroundColor: theme.bg }}
          dir={dir}
        >
          <SheetHeader className="shrink-0">
            <SheetTitle className="pr-10">
              <span className="flex items-center gap-2 text-xl font-bold" style={{ fontFamily: 'Outfit', color: theme.accent }}>
                <MenuIcon size={20} /> {tr('our_menu')}
              </span>
            </SheetTitle>
          </SheetHeader>
          <p className="text-xs text-stone-500 mt-1 shrink-0">{tr('menu_browse_only')}</p>
          {/* Inline language chip row — the floating LanguagePicker pill
              sits behind Radix's modal overlay while this sheet is open, so
              customers had no way to switch language without first closing
              the menu. Rendering the same set of codes here lets them flip
              language in-place; the [lang, menuOpen] effect above refetches
              the menu so the content updates without closing the sheet. */}
          {(() => {
            const known = [defaultLang, ...(availableLangs || [])].filter(Boolean);
            const codes = Array.from(new Set(known));
            if (codes.length <= 1) return null;
            return (
              <div
                className="mt-2 shrink-0 flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1"
                dir="ltr"
                data-testid="paging-menu-lang-chips"
              >
                {codes.map(code => {
                  const active = code === lang;
                  return (
                    <button
                      key={code}
                      type="button"
                      data-testid={`paging-menu-lang-${code}`}
                      onClick={() => setLang(code)}
                      className="h-7 min-w-[36px] px-2 rounded-full text-[11px] font-extrabold uppercase tracking-wider flex items-center justify-center transition-colors shrink-0 border"
                      style={active
                        ? { backgroundColor: theme.primary, color: '#fff', borderColor: theme.primary }
                        : { backgroundColor: '#fff', color: '#78716c', borderColor: '#e7e5e4' }}
                    >
                      {code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            );
          })()}
          {/* Scrollable body: the parent SheetContent is `h-[90vh]
              overflow-hidden flex-col`, so this child is the only thing
              that scrolls. min-h-0 is required to let flex children
              actually shrink and become scrollable on mobile WebKit. */}
          <div className="mt-4 pb-6 flex-1 overflow-y-auto min-h-0">
            {menuLoading && (
              <p className="text-sm text-stone-400 text-center py-12">{tr('loading_menu')}</p>
            )}
            {!menuLoading && menuData && menuData.categories.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-12">{tr('menu_preparing')}</p>
            )}
            {!menuLoading && menuData && menuData.categories.map(cat => {
              const items = (menuData.items || []).filter(i => i.category_id === cat.id);
              if (items.length === 0) return null;
              return (
                <div key={cat.id} className="mb-6" data-testid={`paging-menu-cat-${cat.id}`}>
                  <h3 className="text-base font-bold text-stone-800 uppercase tracking-wider mb-3" style={{ fontFamily: 'Outfit' }}>
                    {cat.name}
                  </h3>
                  <div className="space-y-3">
                    {items.map(item => (
                      <div
                        key={item.id}
                        data-testid={`paging-menu-item-${item.id}`}
                        className="flex items-start gap-3 p-3 rounded-xl bg-white border border-stone-200 shadow-sm"
                      >
                        {item.image_path && (
                          <img
                            src={`${API}/files/${item.image_path}`}
                            alt={item.name}
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="font-bold text-stone-900 truncate">{item.name}</p>
                            <p className="font-bold tabular-nums whitespace-nowrap" style={{ color: theme.primary }}>
                              {formatPrice(item.price || 0, restaurant?.currency || 'USD')}
                            </p>
                          </div>
                          {item.description && (
                            <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{item.description}</p>
                          )}
                          {item.available === false && (
                            <p className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                              {tr('sold_out')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
